import React, {
  useState,
  useCallback,
  useContext,
} from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { SendOutlined, ScheduleOutlined } from '@mui/icons-material';

import {
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';

import {
  DateFormat,
} from '../../../../utils/date';
import { roundToNext5Minutes, getUTCOffset } from '../../../../utils/time';
import { AttachmentType } from '../../../../utils/attachments';
import useToast from '../../../../hooks/useToast';
import ChatChannelContext from '../../../../context/ChatChannelContext';
import ChatMessageRequest, { CHAT_MESSAGE_REQUEST_STATUS } from '../../../../Model/ChatMessageRequest';
import LoadingOverlay from '../../../LoadingOverlay/LoadingOverlay';

import FormScheduleMessage from '../FormScheduleMessage';

import texts from './texts.json';
import {
  ItemContainer,
  MessageDate,
  MessageText,
  ActionsContainer,
  InfoContainer,
  EditContainer,
  InfoText,
  FileAttachmentIcon,
  StyledMicIcon,
  SubText,
  Wrapper,
  OuterContainer,
} from './styles';

const ItemStatus = {
  INITIAL: 'INITIAL',
  EDITING: 'EDITING',
  DELETING: 'DELETING',
  RESENDING: 'RESENDING',
  RESCHEDULING: 'RESCHEDULING',
  DRAFT_DELETING: 'DRAFT_DELETING',
};

const ScheduledMessageItem = ({
  messageRequestDoc,
  isDraftMessagesView,
  deleteOnly,
}) => {
  const {
    text,
    sendAt,
    attachments,
    activityId,
  } = messageRequestDoc;

  const [itemStatus, setItemStatus] = useState(ItemStatus.INITIAL);
  const [isLoading, setIsLoading] = useState(false);

  const { showToast } = useToast();

  const {
    updateScheduledMessage,
    deleteScheduledMessage,
    setScheduledCount,
    sendMessageToUser,
    activeChannel,
    coachTimezone,
    pendingScheduledMessages,
  } = useContext(ChatChannelContext);

  const timezoneOffset = getUTCOffset(coachTimezone || moment.tz.guess());

  const isLastPendingMessage = isDraftMessagesView && pendingScheduledMessages.length === 1;

  const handleSave = useCallback(async (newSendAt, newText, newAttachments, shouldForceSend) => {
    const newStatus = isDraftMessagesView ? CHAT_MESSAGE_REQUEST_STATUS.CREATED : '';
    await updateScheduledMessage(messageRequestDoc, newSendAt, newText, newAttachments, newStatus, shouldForceSend);
    if (isLastPendingMessage) {
      showToast(texts.lastDraftRescheduled);
    }
    setItemStatus(ItemStatus.INITIAL);
  }, [
    updateScheduledMessage,
    messageRequestDoc,
    isDraftMessagesView,
    isLastPendingMessage,
    showToast,
  ]);

  const handleDelete = useCallback(async () => {
    await deleteScheduledMessage(messageRequestDoc);
    setScheduledCount((prevCount) => prevCount - 1);
    if (isLastPendingMessage) {
      showToast(texts.lastDraftDeleted);
    }
    setItemStatus(ItemStatus.INITIAL);
  }, [
    deleteScheduledMessage,
    messageRequestDoc,
    setScheduledCount,
    isLastPendingMessage,
    showToast,
  ]);

  const handleSend = useCallback(async () => {
    setIsLoading(true);
    await sendMessageToUser(activeChannel.userId, text, attachments, undefined, !!activityId);
    await messageRequestDoc.updateFields({ status: CHAT_MESSAGE_REQUEST_STATUS.COMPLETED });
    setIsLoading(false);
    showToast(isLastPendingMessage ? texts.lastDraftSent : texts.messageSent);
  }, [
    sendMessageToUser,
    messageRequestDoc,
    showToast,
    text,
    attachments,
    activeChannel,
    activityId,
    isLastPendingMessage,
  ]);

  const renderItemContent = useCallback(() => {
    switch (itemStatus) {
      case ItemStatus.EDITING:
      case ItemStatus.RESCHEDULING:
        return (
          <EditContainer>
            <FormScheduleMessage
              defaultText={text}
              defaultSendDate={isDraftMessagesView
                ? moment().utcOffset(timezoneOffset)
                : moment(sendAt).utcOffset(timezoneOffset)}
              defaultSendTime={isDraftMessagesView
                ? roundToNext5Minutes(moment().utcOffset(timezoneOffset))
                : moment(sendAt).utcOffset(timezoneOffset)}
              defaultAttachments={attachments}
              onSave={handleSave}
              saveButtonLabel={texts.button.save}
              onCancel={() => setItemStatus(ItemStatus.INITIAL)}
              cancelButtonLabel={texts.button.cancel}
            />
          </EditContainer>
        );
      case ItemStatus.DELETING:
      case ItemStatus.DRAFT_DELETING:
        return (
          <InfoContainer>
            <InfoText>{isDraftMessagesView ? texts.deleteDraftConfirm : texts.deleteConfirm}</InfoText>
          </InfoContainer>
        );
      case ItemStatus.RESENDING:
        return (
          <InfoContainer>
            <InfoText>{texts.resendConfirm}</InfoText>
          </InfoContainer>
        );
      case ItemStatus.INITIAL:
      default:
        return (
          <OuterContainer>
            <Wrapper>
              <InfoContainer>
                <MessageDate>
                  {moment(sendAt).format(DateFormat.DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT)}
                </MessageDate>
                <Tooltip title={text}>
                  <MessageText>{text}</MessageText>
                </Tooltip>
              </InfoContainer>
              <SubText>
                {format(isDraftMessagesView
                  ? texts.selectedDateInCoachTimezoneOfDraft : texts.selectedDateInCoachTimezone, {
                  time: moment(sendAt).utcOffset(timezoneOffset)
                    .format(DateFormat.DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT),
                })}
              </SubText>
            </Wrapper>
            {attachments.length > 0 && (
              <>
                {attachments.some((attachment) => attachment.type === AttachmentType.AUDIO) && (
                  <Tooltip title={texts.tooltip.audio}>
                    <div><StyledMicIcon /></div>
                  </Tooltip>
                )}
                {attachments.some((attachment) => attachment.type !== AttachmentType.AUDIO) && (
                  <Tooltip title={texts.tooltip.attachments}>
                    <div><FileAttachmentIcon /></div>
                  </Tooltip>
                )}
              </>
            )}
          </OuterContainer>
        );
    }
  }, [
    itemStatus,
    text,
    sendAt,
    attachments,
    handleSave,
    isDraftMessagesView,
    timezoneOffset,
  ]);

  const renderActionButtons = useCallback(() => {
    switch (itemStatus) {
      case ItemStatus.EDITING:
      case ItemStatus.RESCHEDULING:
        return <></>;
      case ItemStatus.DELETING:
      case ItemStatus.DRAFT_DELETING:
        return (
          <ActionsContainer>
            <Button
              onClick={() => setItemStatus(ItemStatus.INITIAL)}
              color="info"
            >
              {texts.button.cancel}
            </Button>
            <Button
              onClick={handleDelete}
              color="warning"
            >
              {texts.button.delete}
            </Button>
          </ActionsContainer>
        );
      case ItemStatus.RESENDING:
        return (
          <ActionsContainer>
            <Button
              onClick={() => setItemStatus(ItemStatus.INITIAL)}
              color="info"
            >
              {texts.button.cancel}
            </Button>
            <Button
              onClick={handleSend}
              color="warning"
            >
              {texts.button.send}
            </Button>
          </ActionsContainer>
        );
      case ItemStatus.INITIAL:
      default:
        return (
          <ActionsContainer>
            {!deleteOnly && (
              <Tooltip title={isDraftMessagesView ? texts.tooltip.reScheduled : texts.tooltip.edit}>
                <IconButton
                  onClick={() => setItemStatus(isDraftMessagesView ? ItemStatus.RESCHEDULING : ItemStatus.EDITING)}
                >
                  {isDraftMessagesView ? <ScheduleOutlined /> : <EditIcon />}
                </IconButton>
              </Tooltip>
            )}
            {isDraftMessagesView && !deleteOnly && (
              <Tooltip title={texts.tooltip.sendDraft}>
                <IconButton
                  onClick={() => setItemStatus(ItemStatus.RESENDING)}
                >
                  <SendOutlined />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isDraftMessagesView ? texts.tooltip.draftDelete : texts.tooltip.delete}>
              <IconButton
                onClick={() => setItemStatus(isDraftMessagesView ? ItemStatus.DRAFT_DELETING : ItemStatus.DELETING)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </ActionsContainer>
        );
    }
  }, [
    itemStatus,
    handleDelete,
    isDraftMessagesView,
    handleSend,
    deleteOnly,
  ]);

  return (
    <ItemContainer>
      {renderItemContent()}
      {renderActionButtons()}
      <LoadingOverlay isLoading={isLoading} />
    </ItemContainer>
  );
};

ScheduledMessageItem.propTypes = {
  messageRequestDoc: PropTypes.instanceOf(ChatMessageRequest).isRequired,
  isDraftMessagesView: PropTypes.bool.isRequired,
  deleteOnly: PropTypes.bool,
};

ScheduledMessageItem.defaultProps = {
  deleteOnly: false,
};

export default compose(
  observer,
)(ScheduledMessageItem);
